/**
 * AdminEarningsTab · v2.6.9 batch BL
 * ===================================
 * Supervisor / admin · global earnings rollup with 60-second server cache.
 * Lazy-loaded — the parent only mounts this component when the tab gets focus.
 *
 * Layout:
 *   ◇ Header strip · Today's Earnings hero ($) + manual Refresh
 *   ◇ KPI grid · Today / Week / All Time   (total · count · miles · tips)
 *   ◇ Third-party fees grid · Tech / Franchise / Taxes  (Today/Week/All)
 *   ◇ Top-10 leaderboard · driver leaderboard by today's earnings
 */
import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, DollarSign, ReceiptText, ScrollText, Sparkles, Crown, TrendingUp, RefreshCw } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { fmtUsd } from "@/lib/format";
import useFleetStream from "@/hooks/useFleetStream";
import ComplianceLedgerCard from "@/components/admin/ComplianceLedgerCard";
import DepositReconciliationPanel from "@/components/admin/DepositReconciliationPanel";

const BUCKETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "all_time", label: "All Time" },
];

export default function AdminEarningsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // v2.6.9 batch BN · Tap the dispatch fleet stream so the moment a ride is
  // initiated (`driver_dispatched`), we force-refresh the Today's Earnings
  // / Third-Party Fees figures — important for end-of-day settlement.
  const token = typeof window !== "undefined" ? localStorage.getItem("rtg_token") : null;
  const { events } = useFleetStream({ role: "dispatch", token, active: true });
  const lastEventTsRef = useRef(0);

  const load = (force = false) => {
    const fn = force ? setRefreshing : setLoading;
    fn(true);
    // v2.7.0 batch DN · Always append a cache-bust token in addition to
    // force=1, so any HTTP cache between client and origin can't serve a
    // stale snapshot. The backend treats force=1 as both "bypass" AND "clear"
    // so non-force polls within the next 60s also get fresh values.
    const params = new URLSearchParams();
    if (force) {
      params.set("force", "1");
      params.set("_", String(Date.now()));
    }
    const qs = params.toString();
    adminApi.get(`/admin/earnings/global${qs ? `?${qs}` : ""}`, {
      headers: force ? { "Cache-Control": "no-cache", Pragma: "no-cache" } : undefined,
    })
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load earnings"))
      .finally(() => fn(false));
  };

  useEffect(() => { load(false); }, []);
  // v2.6.9 batch BN · Poll every 15s for tighter real-time feedback.
  // v2.7.0 batch DO · Dropped from 30s → 15s per user requirement "refresh
  // every 10–30 seconds constantly". Backend has its own 60s cache that
  // also auto-busts on every trip_completed broadcast, so the actual heavy
  // aggregation cost stays near-zero — these polls hit the cached payload
  // when the dashboard is idle, and the live recomputed payload when a
  // trip just landed. Pause when the tab is hidden to spare bandwidth.
  useEffect(() => {
    let t = null;
    const startPolling = () => {
      if (t) clearInterval(t);
      t = setInterval(() => {
        if (document.visibilityState === "visible") load(false);
      }, 15000);
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        // Foreground after background — immediately pull fresh values.
        load(false);
        startPolling();
      } else if (t) {
        clearInterval(t);
        t = null;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    startPolling();
    return () => {
      if (t) clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // v2.6.9 batch BN · When a ride is initiated (driver dispatched), force a
  // fresh aggregation immediately so the supervisor sees the new trip
  // reflected in Tech/Franchise/Taxes "Today" without waiting for the 30s tick.
  // v2.7.0 batch DN · Also listen for `trip_completed` events so the
  // moment any driver closes a trip the supervisor sees the new earnings
  // total without manual Force Refresh.
  useEffect(() => {
    if (!events?.length) return;
    const latest = events[0];
    if (latest?.type !== "driver_dispatched" && latest?.type !== "trip_completed") return;
    const ts = new Date(latest.ts || Date.now()).getTime();
    if (ts <= lastEventTsRef.current) return;
    lastEventTsRef.current = ts;
    load(true);
  }, [events]);

  return (
    <div className="space-y-5" data-testid="admin-earnings-tab">
      {/* v2.7.56+E33 · Deposit reconciliation panel — surfaces every driver
          End-of-Day submission and lets supervisors verify / close / escalate. */}
      <DepositReconciliationPanel/>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-extrabold flex items-center gap-2"><DollarSign className="h-5 w-5 text-amber-400"/>Earnings Overview</h2>
          <p className="text-xs text-zinc-400">Cached 60s server-side · auto-refreshes every 30s.</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.cached && (
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]" data-testid="admin-earnings-cache-badge">CACHED · {data.cache_age_seconds || 0}s</Badge>
          )}
          <Button data-testid="admin-earnings-refresh" onClick={() => load(true)} disabled={refreshing} variant="outline" className="bg-zinc-950 border-zinc-700">
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin"/> : <><RefreshCw className="h-4 w-4 mr-1"/>Force Refresh</>}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="bg-red-500/10 border-red-500/40 text-red-200 p-4 text-sm" data-testid="admin-earnings-error">{error}</Card>
      )}

      {loading && !data && (
        <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto"/>
          <div className="mt-2 text-xs uppercase tracking-widest">Loading aggregation…</div>
        </Card>
      )}

      {data && (
        <>
          {/* v2.7.0 batch DO+++++++ · Premium hero with neon-amber glow + pulse */}
          <Card className="hero-revenue-card glow-amber p-6" data-testid="admin-earnings-today-hero">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Total Earnings · Today</div>
            <div className="hero-revenue-figure meter-display text-5xl sm:text-6xl font-extrabold leading-none mt-2 text-amber-300" data-testid="admin-earnings-today-total">
              {fmtUsd(data.today?.total)}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              {data.today?.count || 0} trip{data.today?.count === 1 ? "" : "s"} · {Math.round(data.today?.miles || 0)} mi · avg {fmtUsd(data.today?.avg_per_trip)} · tips {fmtUsd(data.today?.tips)}
            </div>
          </Card>

          {/* Fares + counts grid — premium tinted buckets */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-earnings-buckets">
            {BUCKETS.map((b, i) => {
              const v = data[b.key] || {};
              const tints = ["sky", "mint", "amber"];
              const tint = tints[i] || "amber";
              return (
                <Card key={b.key} data-testid={`admin-earnings-bucket-${b.key}`} className={`stat-card-base stat-card-tint-${tint} p-4`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{b.label}</div>
                    <Sparkles className="h-3.5 w-3.5 text-amber-400/60"/>
                  </div>
                  <div className="meter-display text-3xl font-extrabold mt-1 text-amber-300 tabular-nums">{fmtUsd(v.total)}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{v.count || 0} trips · {Math.round(v.miles || 0)} mi · avg {fmtUsd(v.avg_per_trip)}</div>
                  <div className="grid grid-cols-2 gap-1.5 mt-2 text-[11px]">
                    <div className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200" data-testid={`admin-earnings-${b.key}-tips`}>
                      <div className="text-[8px] uppercase tracking-widest opacity-70">Tips</div>
                      <div className="meter-display font-bold">{fmtUsd(v.tips)}</div>
                    </div>
                    <div className="px-2 py-1 rounded bg-zinc-800/60 border border-zinc-700 text-zinc-300">
                      <div className="text-[8px] uppercase tracking-widest opacity-70">Cash · Card</div>
                      <div className="meter-display font-bold text-[11px]">{fmtUsd(v.cash_collected)} · {fmtUsd(v.card_collected)}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Third-party fees grid — Tech Fees + Franchise + Earned Taxes */}
          <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden" data-testid="admin-earnings-fees-card">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <ReceiptText className="h-4 w-4 text-amber-400"/>
                <h3 className="text-sm font-bold uppercase tracking-widest">Third-Party Fees Earned</h3>
              </div>
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500" data-testid="admin-earnings-fees-refresh-meta">
                <RefreshCw className="h-3 w-3"/>
                <span>Live · 30s · on ride start</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-earnings-fees-table">
                <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Fee Type</th>
                    <th className="text-right px-4 py-2 font-bold">Today</th>
                    <th className="text-right px-4 py-2 font-bold">Week</th>
                    <th className="text-right px-4 py-2 font-bold">All Time</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {[
                    { label: "Technology Fees", k: "tech_fee", testid: "tech" },
                    { label: "Franchise Fees", k: "franchise_fee", testid: "franchise" },
                    { label: "Earned Taxes", k: "taxes", testid: "taxes" },
                  ].map((row) => (
                    <tr key={row.k} className="border-t border-zinc-800" data-testid={`admin-earnings-fee-row-${row.testid}`}>
                      <td className="px-4 py-2.5 text-zinc-400 text-xs">{row.label}</td>
                      <td className="px-4 py-2.5 text-right meter-display">{fmtUsd(data.today?.[row.k])}</td>
                      <td className="px-4 py-2.5 text-right meter-display">{fmtUsd(data.week?.[row.k])}</td>
                      <td className="px-4 py-2.5 text-right meter-display text-amber-400 font-bold">{fmtUsd(data.all_time?.[row.k])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* v2.7.5 · Compliance Ledger embedded directly under the
              Third-Party Fees Earned summary. Provides the per-24-hour
              historical block view with red unpaid styling + Mark Paid
              modal (Cash App / Debit Transaction). */}
          <ComplianceLedgerCard/>

          {/* Top-10 driver leaderboard for today */}
          <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden" data-testid="admin-earnings-leaderboard-card">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-400"/>
              <h3 className="text-sm font-bold uppercase tracking-widest">Top 10 Drivers · Today</h3>
            </div>
            {(data.leaderboard || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-xs text-zinc-500" data-testid="admin-earnings-leaderboard-empty">
                No driver trips logged yet today. Once drivers complete trips, the leaderboard will populate live.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-800" data-testid="admin-earnings-leaderboard">
                {(data.leaderboard || []).map((row, idx) => (
                  <li key={row.driver_id} className="flex items-center justify-between px-4 py-2.5 hover:bg-zinc-800/40" data-testid={`admin-earnings-leaderboard-${idx}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${idx === 0 ? "bg-amber-400 text-zinc-950" : idx < 3 ? "bg-amber-400/30 text-amber-200" : "bg-zinc-800 text-zinc-400"}`}>
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" data-testid={`admin-earnings-leader-${idx}-name`}>{row.driver_name || row.driver_id?.slice(0, 8)}</div>
                        <div className="text-[10px] text-zinc-500">{row.count} trip{row.count === 1 ? "" : "s"} · tips {fmtUsd(row.tips)}</div>
                      </div>
                    </div>
                    <div className="meter-display text-base font-extrabold text-amber-400" data-testid={`admin-earnings-leader-${idx}-total`}>{fmtUsd(row.total)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <div className="text-[10px] text-zinc-600 text-center uppercase tracking-widest" data-testid="admin-earnings-meta">
            Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"} · refreshes every 30s · server cache 60s
          </div>
        </>
      )}
    </div>
  );
}
